import React from "react";
import { useState, useRef, useEffect } from "react";

import Share from "../assets/Share.png";
import Vectorc from "../assets/Vectorc.png";
import Broadcast from "../assets/Broadcast.png";
import three_phones from "../assets/three_phones.png";
import audiencestadium from "../assets/audiencestadium.png";
import laptopscreens from "../assets/laptopscreens.png";
import laptopmobiles from "../assets/laptopmobiles.png";
import { Link } from "react-router-dom";

import { User, Camera, Gift, Settings } from "lucide-react";
import { FanContent } from "./FanContent";

export default function CaptureShareBroadcast() {
  const [index, setIndex] = useState(0);
  const wrapperRef = useRef(null);
  const indexRef = useRef(0);
  const [direction, setDirection] = useState("down");

  const fanContents = [
    {
      title: "Vupop for Fans",
      text: "Vupop is simple and free to join. Fans and sports creators can:",
      bullets: [
        "Capture fan content with mobile video or clips",
        "Upload directly in the app",
        "Choose how and where it can be used",
        "Get connected with broadcasters, teams and creators",
      ],
      image: three_phones,
      text1:
        "Your fan content can be seen by sports media professionals. If they want to use your clip, they can export it and immediately license it and use it in their broadcast whilst you get rewarded",
      text2:
        "This solves a key problem in sports broadcasting today: finding great fan content, getting permission at scale, and doing it fast enough for use in shows and highlights.",
      button: <Link to="/VupopForFans">Explore Fan Features</Link>,
    },
    {
      title: "How Vupop Helps Broadcasters and Sports Media",
      text: "Broadcasters want authentic audience views. They want the emotion that only real fans can capture. But it takes work to discover clips, reach out to fans, and sort permissions, especially during live or near-live coverage.",
      bullets: [
        "Gathering fan content in one place",
        "Helping broadcasters discover clips quickly",
        "Facilitating clear permission from fans at point of sign up",
        "Supporting fast media export workflows for broadcast use",
      ],
      //second picture
      image: laptopscreens,
      text1:
        "This fast, permission-based flow gives broadcasters more choice without adding risk or delay. In a world of mass content and AI-generated media, real fan content remains a premium source of truth and emotional connection.",
      text2: "",
      button: (
        <Link to="/VupopForBroadcasters">Explore Broadcaster Features</Link>
      ),
    },
    {
      title: "Vupop for Brands",
      text: "Brands want to connect with real fans in real moments. Fan content helps brands understand how people use and react to their products inside sports culture. Vupop makes it easy for brands to invite fans to share their point of view and be rewarded for their loyalty.",
      bullets: [
        "Encourage fans to share product moments",
        "Support authentic brand-to-fan engagement",
        "Reward fans for participation and loyalty",
        "Build trust through real voices and real experiences",
      ],
      //third picture
      image: laptopmobiles,
      text1:
        "This helps brands strengthen their relationship with fans and reach audiences in a way that feels honest, human and connected to live sport.",
      text2: "",
      button: <Link to="/VupopForBrands">Explore Brand Features</Link>,
    },
    {
      title: "Vupop for Clubs and Rights Holders",
      text: "Clubs, rights holders and sports organisations want to build loyalty. Fan content gives them a direct view of how supporters feel and behave. VUPOP helps these groups gather and understand fan content so they can:",
      bullets: [
        "Build richer fan engagement strategies",
        "Support community storytelling",
        "Highlight fan voices in official channels",
        "Learn from real supporter reactions",
      ],
      //fourth picture
      image: audiencestadium,
      text1:
        "This supports stronger relationships between clubs and their audiences, giving more insight into what fans truly value.",
      text2: "",
      button: null,
    },
  ];
  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      const scrolled = -rect.top;
      const totalScrollable = wrapperHeight - viewportHeight;

      if (scrolled < 0 || scrolled > totalScrollable) return;

      const newIndex = Math.min(
        Math.floor((scrolled / totalScrollable) * fanContents.length),
        fanContents.length - 1,
      );

      if (newIndex !== indexRef.current) {
        setDirection(newIndex > indexRef.current ? "down" : "up");
        indexRef.current = newIndex;
        setIndex(newIndex);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="bg-black text-white min-h-screen">
      <section className="w-[100%] h-[auto] mt-10 mx-auto px-4 sm:px-6 md:px-10 py-12 sm:py-16 mb-12 sm:mb-20">
        <h1
          style={{ fontFamily: "Norwester" }}
          className="text-[60px] sm:text-[28px] md:text-[36px] lg:text-[40px] xl:text-[52px]  font-semibold text-center uppercase tracking-wider mb-12 sm:mb-10"
        >
          CAPTURE. <span className="text-[#FFF528]">SHARE.</span> BROADCAST.
        </h1>

        <div className="flex sm:flex-col md:flex-row gap-[60px] md:gap-12 mb-12 px-12 items-start sm:px-6">
          <div className="flex-1 text-left sm:w-[100%] w-full sm:text-center">
            <div className="text-5xl mb-6 w-[44px] h-[44px]">
              <img src={Vectorc} alt="Capture" />
            </div>
            <p className="text-[18px] xl:text-[18px] lg:text-[17px] md:text-[15px] sm:text-[14px] leading-relaxed text-gray-300">
              Fans are the heartbeat of sport. Their reactions, emotion, and
              unique point of view bring real feeling to every moment. With
              Vupop, fans can capture authentic match-day moments as they
              happen.
            </p>
          </div>

          <div className="flex-1 text-left sm:w-[100%] w-full sm:text-center">
            <div className="text-5xl mb-6 w-[44px] h-[44px]">
              <img src={Share} alt="Share" />
            </div>
            <p className="text-[18px] xl:text-[18px] lg:text-[17px] md:text-[15px] sm:text-[14px] leading-relaxed text-gray-300">
              VUPOP makes it easy to upload and share fan content in real time.
              Fans choose how their clips can be used, keeping control while
              contributing to something bigger than their own screen.
            </p>
          </div>

          <div className="flex-1 text-left sm:w-[100%] w-full sm:text-center">
            <div className="text-5xl mb-6 w-[44px] h-[44px]">
              <img src={Broadcast} alt="Broadcast" />
            </div>
            <p className="text-[18px] xl:text-[18px] lg:text-[17px] md:text-[15px] sm:text-[14px] leading-relaxed text-gray-300">
              VUPOP connects great fan moments with broadcasters and sports
              media teams. Approved clips can be used in live broadcasts,
              community highlights, and digital stories.
            </p>
          </div>
        </div>

        <div className="flex lg:flex-row sm:flex-row justify-center gap-[8px]">
          <Link to="VupopForBroadcasters">
            <button className="bg-[#FFF528] font-['Norwester'] text-black px-[20px] py-[12px] rounded-md uppercase text-[22px] sm:text-[13px] md:text-[14px] lg:text-[16px] xl:text-[17px] min-w-[160px] sm:min-w-[120px] hover:bg-[#FFF528]/70">
              For Broadcasters
            </button>
          </Link>
          <Link to="VupopForFans">
            <button className="bg-[#FFF528] font-['Norwester'] text-black px-[20px] py-[12px] rounded-md uppercase text-[22px] sm:text-[13px] md:text-[14px] lg:text-[16px] xl:text-[17px] min-w-[160px] sm:min-w-[120px] hover:bg-[#FFF528]/70">
              For Fans
            </button>
          </Link>
        </div>
      </section>

      <section className="hidden lg:block px-4 sm:px-6 md:px-10 py-8 space-y-6">
        {fanContents.map((item, i) => (
          <div
            key={i}
            className="bg-[#111315] rounded-3xl p-8 lg:p-6 md:p-5 sm:p-4"
          >
            <div className="bg-[#181A1D] rounded-2xl border border-[#40444D] overflow-hidden mb-6 flex items-center justify-center">
              <img
                src={item.image}
                alt={item.title}
                className="w-full max-h-[260px] lg:max-h-[220px] md:max-h-[200px] sm:max-h-[180px] object-contain"
              />
            </div>
            <h2
              style={{ fontFamily: "Norwester" }}
              className="text-[26px] xl:text-[24px] lg:text-[22px] md:text-[20px] sm:text-[18px] uppercase mb-4 leading-tight"
            >
              {item.title}
            </h2>
            <p className="text-[#C4C7CC] mb-4 text-[20px] xl:text-[18px] lg:text-[16px] md:text-[15px] sm:text-[14px] leading-relaxed">
              {item.text}
            </p>
            <ul className="space-y-2 mb-4 text-[#C4C7CC] text-[20px] xl:text-[18px] lg:text-[16px] md:text-[15px] sm:text-[14px] leading-relaxed">
              {item.bullets.map((b, j) => (
                <li key={j}>• {b}</li>
              ))}
            </ul>
            <p className="text-[#C4C7CC] mb-4 text-[1px] xl:text-[18px] lg:text-[16px] md:text-[15px] sm:text-[14px] leading-relaxed">
              {item.text1}
            </p>
            {item.text2 ? (
              <p className="text-[#C4C7CC] mb-4 text-[20px] xl:text-[18px] lg:text-[16px] md:text-[15px] sm:text-[14px] leading-relaxed">
                {item.text2}
              </p>
            ) : null}
            {item.button && (
              <button className="mt-2 bg-[#FFF528] hover:bg-[#FFF528]/70 transition-colors font-['Norwester'] text-black px-6 py-3 text-[16px] sm:text-[14px] rounded-md uppercase">
                {item.button}
              </button>
            )}
          </div>
        ))}
      </section>

      <div
        ref={wrapperRef}
        style={{ height: `${(fanContents.length + 1) * 100}vh` }}
        className="relative lg:hidden"
      >
        <section className="sticky top-0 h-screen w-full px-8 py-8">
          <div className="bg-[#111315] rounded-3xl px-8 sm:px-10 md:px-12 py-8 md:py-10 h-full">
            <div className="flex lg:flex-row md:flex-col sm:flex-col gap-8 lg:gap-12 h-full">
              <div className="flex-1 w-full relative flex flex-col justify-center">
                <div className="md:flex absolute left-[-45px] top-5 flex-col items-center">
                  {[User, Camera, Gift, Settings].map((Icon, i) => {
                    const isActive = i === index;

                    return (
                      <div
                        key={i}
                        className={`relative flex items-center justify-center
          transition-all duration-500 ease-in-out
          ${isActive ? "w-10 h-10 bg-white" : "w-10 h-10 bg-[#1B1E22]"}
          rounded-full shadow-md overflow-hidden`}
                      >
                        <span
                          className={`absolute inset-0 bg-white origin-top
            transition-transform duration-500 ease-in-out
            ${isActive ? "scale-y-100" : "scale-y-0"}`}
                        />

                        <Icon
                          className={`relative z-10 transition-colors duration-300
            ${isActive ? "text-black w-5 h-5" : "text-gray-400 w-4 h-4"}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="w-full">
                  <div
                    key={index}
                    className={`transition-all duration-700 ease-in-out
      ${direction === "down" ? "animate-slide-in-up" : "animate-slide-in-down"}`}
                  >
                    <h2
                      style={{ fontFamily: "Norwester" }}
                      className="text-[48px] xl:text-[36px] lg:text-[28px] md:text-[24px] sm:text-[22px] uppercase mb-4 leading-tight"
                    >
                      {fanContents[index].title}
                    </h2>

                    <p className="text-[#C4C7CC] mb-3 text-[18px] xl:text-[16px] lg:text-[14px] md:text-[14px] sm:text-[13px] leading-relaxed">
                      {fanContents[index].text}
                    </p>

                    <ul className="space-y-3 mb-6 text-[#C4C7CC] text-[18px] xl:text-[16px] lg:text-[14px] md:text-[14px] sm:text-[13px] leading-relaxed">
                      {fanContents[index].bullets.map((b, i) => (
                        <li key={i}>• {b}</li>
                      ))}
                    </ul>

                    <p className="text-[#C4C7CC] mb-6 text-[18px] xl:text-[16px] lg:text-[14px] md:text-[14px] sm:text-[13px] leading-relaxed">
                      {fanContents[index].text1}
                    </p>

                    <p className="text-[#C4C7CC] mb-6 text-[18px] xl:text-[16px] lg:text-[14px] md:text-[14px] sm:text-[13px] leading-relaxed">
                      {fanContents[index].text2}
                    </p>
                  </div>

                  {fanContents[index].button && (
                    <button
                      className={`mt-4 bg-[#FFF528] hover:bg-[#FFF528]/70 transition-colors font-thin font-['Norwester'] text-black
        px-5 py-3 text-[16px] xl:text-[14px] lg:text-[13px] sm:text-[13px]
        rounded-md uppercase  duration-700 ease-in-out
        ${direction === "down" ? "animate-slide-in-up" : "animate-slide-in-down"}`}
                    >
                      {fanContents[index].button}
                    </button>
                  )}
                </div>
              </div>

              <div
                key={index}
                className={`flex-1 bg-[#181A1D] flex items-center justify-center
  rounded-3xl border border-[#40444D] min-h-[250px] sm:min-h-[300px] lg:min-h-0 lg:self-stretch overflow-hidden
  transition-all duration-700 ease-in-out
  ${direction === "down" ? "animate-slide-in-up" : "animate-slide-in-down"}`}
              >
                <img
                  key={index}
                  src={fanContents[index].image}
                  alt="Fan feature"
                  className={`max-w-full max-h-full object-contain ${
                    direction === "down" ? "animate-up" : "animate-down"
                  }`}
                />
              </div>
            </div>
          </div>
        </section>
      </div>

    </div>
  );
}
